import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';

import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { ObjectType, Package } from '@/@types/graphql-types';
import { useLanguageStore } from '@/helpers/composables/useStores';
import { priceByCountry, exp2AffiliateLinksParams } from '@/components/experiments/AppleFreeTrialExp_2/data';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { getOfferImpressionContext, observableOptions } from '@/helpers/composables/useOfferImpressionTracking';
import AppleTVFreeTrialExp, { AppleFreeTrialExp2Variants } from '@/components/experiments/AppleFreeTrialExp_2';
import { ImpressionTrackingEvents } from '@/enums/events';
import { BuyboxParam, OfferLocation, trackClickOutGraphql } from '@/helpers/offer-helper';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { TitleDetailsNode } from '@/interfaces/title-details-graphql';
import { usePromotion } from '@/helpers/composables/usePromotion';
import { useApolloData } from '@/helpers/composables/useApolloData';
import { TitleOffers } from '@/interfaces/buybox';
import { TitleOfferFragment } from '@/components/buybox/graphql/fragments/Offer.fragment';

interface PromotionRowProps {
	title: TitleDetailsNode;
	allProvidersById: Record<number, Package> | null;
	isActive: boolean;
	offers: TitleOffers;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ExpAppleFreeTrialRow',
  props: {
    title: null,
    allProvidersById: null,
    isActive: { type: Boolean },
    offers: null
  },
  setup(__props: any) {

const props = __props as {
	title: TitleDetailsNode;
	allProvidersById: Record<number, Package> | null;
	isActive: boolean;
	offers: TitleOffers;
}

const { country } = useLanguageStore();


const { promotionOffer, promotionCatalogue } = usePromotion({
	title: props.title,
	offers: () => {
		const { free = [], stream } = props.offers;
		return [...free, ...stream];
	},
});

const observeVisibilityOptions = {
	...observableOptions,
	once: true,
	callback: (isVisible: boolean) => onVisibilityChanged(isVisible),
};

const expPromotionOffer = computed(() => {
	// in case of the experiment is active and the promotion offer is not Apple TV+ we need to get the Apple TV+ offer.
	if (promotionOffer.value?.package?.packageId !== 350) {
		const appleOffer = promotionCatalogue.value?.get(350);
		if (appleOffer) {
			return appleOffer.offer(null, country.value);
		}
	}
	return promotionOffer.value;
});

const clickoutLink = computed(() => {
	if (!expPromotionOffer.value) return undefined;
	return new ClickoutUrl(expPromotionOffer.value)
		.set('uct_country', country.value)
		.set('uct_buybox', BuyboxParam.Normal)
		.set('uct_ct', exp2AffiliateLinksParams[AppleFreeTrialExp2Variants.VARIANT_1]['freeTrial'].ct)
		.set('uct_itsct', exp2AffiliateLinksParams[AppleFreeTrialExp2Variants.VARIANT_1]['freeTrial'].itsct)
		.toString();
});

function onVisibilityChanged(isVisible: boolean) {
	if (!isVisible) return;
	TrackingHelper.trackEvent(
		ImpressionTrackingEvents.OFFER,
		{
			action: 'impression',
			nonInteraction: true,
			value: OfferLocation.FREE_TRIAL,
			label: 'provider',
		},
		[getOfferImpressionContext(expPromotionOffer.value as TitleOfferFragment)]
	);
	AppleTVFreeTrialExp.trackEvent({
		action: 'impression',
		label: 'free_trial',
	});
}

function onClickOut() {
	trackClickOutGraphql({
		offer: expPromotionOffer.value as TitleOfferFragment,
		objectType: props.title.objectType,
		objectId: props.title.objectId,
		offerLocation: OfferLocation.FREE_TRIAL,
		contentType: undefined,
	});

	AppleTVFreeTrialExp.trackEvent({
		action: 'click',
		label: 'free_trial',
	});
}

return { __sfc: true,country, props, promotionOffer, promotionCatalogue, observeVisibilityOptions, expPromotionOffer, clickoutLink, onVisibilityChanged, onClickOut, ProviderIcon, ObjectType, priceByCountry }
}

})